import React, { useState, useRef } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Avatar,
    Grid,
    TextField,
    Divider,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import TEXT from './text';

const Profile = () => {
    const dispatch = useDispatch();
    const language = useSelector(state => state.App_Settings.language);
    const userMetadata = useSelector(state => state.UserMetaData);
    
    const [editMode, setEditMode] = useState(false);
    const [firstName, setFirstName] = useState(userMetadata.firstName || '');
    const [lastName, setLastName] = useState(userMetadata.lastName || '');
    const [profileImage, setProfileImage] = useState(userMetadata.profileImage || null);
    const [newProfileImage, setNewProfileImage] = useState(null);
    const [newProfileImageBase64, setNewProfileImageBase64] = useState(null);
    const [faceDescriptor, setFaceDescriptor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const fileInputRef = useRef(null);

    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check file size (5MB max)
        if (file.size > 5242880) {
            setError('Image size must be less than 5MB');
            return;
        }

        setNewProfileImage(file);
        
        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = () => {
            setNewProfileImageBase64(reader.result);
            setProfileImage(reader.result);
        };
        reader.readAsDataURL(file);

        // Detect face
        try {
            const image = await window.faceapi.bufferToImage(file);
            const detection = await window.faceapi
                .detectSingleFace(image)
                .withFaceLandmarks()
                .withFaceDescriptor();

            if (detection) {
                setFaceDescriptor(Array.from(detection.descriptor));
                console.log('[Profile] Face descriptor detected');
            } else {
                console.warn('[Profile] No face detected in image');
                setError('No face detected in the image. Please upload a clear photo.');
            }
        } catch (error) {
            console.error('[Profile] Error detecting face:', error);
            setError('Error processing image. Please try again.');
        }
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Update profile API call
            const response = await fetch(window.devMode ? 'http://localhost:80/update_profile' : '/update_profile', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userMetadata.email,
                    firstName,
                    lastName,
                    profileImage: newProfileImageBase64 || profileImage,
                    profileImageDescriptor: faceDescriptor
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                // Update Redux store
                dispatch({
                    type: 'SET_USER_METADATA',
                    payload: {
                        firstName,
                        lastName,
                        fullName: `${firstName} ${lastName}`,
                        profileImage: newProfileImageBase64 || profileImage,
                        profileImageDescriptor: faceDescriptor
                    }
                });

                // Update localStorage
                const storedMetadata = JSON.parse(localStorage.getItem('userMetadata') || '{}');
                const updatedMetadata = {
                    ...storedMetadata,
                    firstName,
                    lastName,
                    fullName: `${firstName} ${lastName}`,
                    profileImage: newProfileImageBase64 || profileImage,
                    profileImageDescriptor: faceDescriptor
                };
                localStorage.setItem('userMetadata', JSON.stringify(updatedMetadata));

                setSuccess('Profile updated successfully!');
                setEditMode(false);
                setNewProfileImage(null);
                setNewProfileImageBase64(null);
            } else {
                setError(data.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('[Profile] Error updating profile:', error);
            setError('Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setEditMode(false);
        setFirstName(userMetadata.firstName || '');
        setLastName(userMetadata.lastName || '');
        setProfileImage(userMetadata.profileImage || null);
        setNewProfileImage(null);
        setNewProfileImageBase64(null);
        setError(null);
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom>
                    {TEXT.ProfileTitle[language]}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Profile Image Section - Left Column on Desktop, Top on Mobile */}
                    <Grid item xs={12} md={4} display="flex" flexDirection="column" alignItems="center">
                        <Box position="relative">
                            <Avatar
                                src={profileImage}
                                alt={userMetadata.fullName}
                                sx={{
                                    width: 150,
                                    height: 150,
                                    mb: 2,
                                    border: '4px solid',
                                    borderColor: 'primary.main'
                                }}
                            />
                            {editMode && (
                                <Tooltip title={TEXT.ChangeProfileImage[language]}>
                                    <IconButton
                                        color="primary"
                                        aria-label="upload picture"
                                        component="label"
                                        sx={{
                                            position: 'absolute',
                                            bottom: 10,
                                            right: -10,
                                            backgroundColor: 'white',
                                            '&:hover': {
                                                backgroundColor: 'grey.200'
                                            }
                                        }}
                                    >
                                        <PhotoCameraIcon />
                                        <input
                                            ref={fileInputRef}
                                            hidden
                                            accept="image/*"
                                            type="file"
                                            onChange={handleImageChange}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Grid>

                    {/* Profile Information - Right Column on Desktop, Bottom on Mobile */}
                    <Grid item xs={12} >
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    {TEXT.PersonalInformation[language]}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>
                        </Grid>
                        <Grid container spacing={3}>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={TEXT.FirstName[language]}
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    disabled={!editMode}
                                    variant={editMode ? "outlined" : "filled"}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={TEXT.LastName[language]}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    disabled={!editMode}
                                    variant={editMode ? "outlined" : "filled"}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={TEXT.Email[language]}
                                    value={userMetadata.email || ''}
                                    disabled
                                    variant="filled"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={TEXT.UserID[language]}
                                    value={userMetadata.uid || ''}
                                    disabled
                                    variant="filled"
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
                        {!editMode ? (
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => setEditMode(true)}
                            >
                                {TEXT.EditProfile[language]}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    disabled={loading}
                                >
                                    {TEXT.Cancel[language]}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                                    onClick={handleSave}
                                    disabled={loading || !firstName || !lastName}
                                >
                                    {TEXT.SaveChanges[language]}
                                </Button>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default Profile;
